import type { ComponentPropsWithRef, ReactNode } from "react";

import { cn } from "../lib/cn";

/**
 * Code en ligne. Reprend exactement le traitement `.prose-inline-code`
 * de onenexty.com : fond en retrait, bordure fine, rayon `sm`.
 *
 * `selectable` : au doigt, le système coupe la sélection native partout par
 * défaut (voir la politique de sélection tactile du thème). Un extrait de code
 * fait précisément partie des exceptions — il est là pour être emporté.
 */
export function Code({ className, ...props }: ComponentPropsWithRef<"code">) {
  return (
    <code
      className={cn(
        "rounded-sm border border-border/60 bg-surface-sunken px-1.5 py-0.5 inset-shadow-control",
        "font-mono text-[0.85em] text-fg selectable",
        className,
      )}
      {...props}
    />
  );
}

export interface CodeBlockProps extends Omit<ComponentPropsWithRef<"pre">, "children" | "title"> {
  /** Nom de fichier ou langage affiché en en-tête. */
  title?: ReactNode;
  children: string;
}

/**
 * Bloc de code. Volontairement sans coloration syntaxique : une dépendance
 * lourde de moins, et le rendu reste cohérent dans les deux thèmes.
 */
export function CodeBlock({ className, title, children, ...props }: CodeBlockProps) {
  return (
    <figure className="overflow-hidden rounded-xl border border-border bg-surface-sunken">
      {title && (
        <figcaption className="border-b border-border px-4 py-2 font-mono text-xs text-fg-muted">
          {title}
        </figcaption>
      )}
      <pre
        className={cn(
          "overflow-x-auto p-4 font-mono text-sm leading-relaxed text-fg selectable",
          className,
        )}
        {...props}
      >
        <code>{children.trim()}</code>
      </pre>
    </figure>
  );
}
